import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGenerateObjectiveDraft } from './useGenerateObjectiveDraft';
import { draftDescriptionToHtml } from '../objectiveDraft';
import { hasOnlyReplaceableKeyResults } from '../objectiveFormValues';
import { DEFAULT_KEY_RESULT } from '../validation';

import type { KeyResultFormData, ObjectiveFormData } from '../validation';
import type { ObjectiveDraft, ObjectiveDraftLanguage } from '@/types/objectives';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { FeatureFlag } from '@/env';
import { useFeatureFlag } from '@/hooks/utils/useFeatureFlag';
import { showSuccessToast } from '@/plugins/Toast';

interface UseObjectiveAiDraftParams {
    getValues: UseFormGetValues<ObjectiveFormData>;
    setValue: UseFormSetValue<ObjectiveFormData>;
    /** The description editor only reads its value on mount, so it is re-seeded after a draft. */
    onDescriptionReplaced: () => void;
}

/** "Generate with AI": the draft window, the generation request and the draft applied to the form. */
export const useObjectiveAiDraft = ({
    getValues,
    setValue,
    onDescriptionReplaced,
}: UseObjectiveAiDraftParams) => {
    const { t } = useTranslation();
    // While the flag is off the button stays visible, disabled, with its "coming soon" tooltip. The
    // configuration answers before the page paints, so no pending state is shown meanwhile.
    const { isFeatureEnabled } = useFeatureFlag();
    const { generate } = useGenerateObjectiveDraft();
    // The key results the last applied draft wrote, so a regenerated draft can tell its own rows,
    // which it replaces, from rows the manager wrote or reworked since, which it never touches.
    const appliedKeyResultsRef = useRef<readonly KeyResultFormData[]>([]);
    // Whether the form had key results of its own is read when the window opens, so the preview can
    // say the suggestions will stay out, without watching every key result keystroke.
    const [modal, setModal] = useState({ isOpen: false, hasKeyResults: false });

    const canReplaceKeyResults = () =>
        hasOnlyReplaceableKeyResults(getValues('keyResults'), appliedKeyResultsRef.current);

    const open = () =>
        setModal({
            isOpen: true,
            hasKeyResults: !canReplaceKeyResults(),
        });

    const close = () => setModal((current) => ({ ...current, isOpen: false }));

    const generateDraft = (userIntent: string, language: ObjectiveDraftLanguage) =>
        generate({
            userIntent,
            language,
            pillarIds: getValues('pillarIds') ?? [],
            employeeId: getValues('employeeIds')[0],
        });

    // The draft always takes the title and description the manager previewed and accepted. Its key
    // results only fill a list with nothing of the manager's own: blank rows, or the rows a previous
    // draft filled and nobody edited since. Rows the manager wrote are never replaced.
    const applyDraft = (draft: ObjectiveDraft) => {
        setValue('title', draft.title, { shouldValidate: true, shouldDirty: true });
        setValue('description', draftDescriptionToHtml(draft.description), {
            shouldValidate: true,
            shouldDirty: true,
        });
        if (draft.keyResults.length > 0 && canReplaceKeyResults()) {
            const keyResults = draft.keyResults.map((keyResult) => ({
                ...DEFAULT_KEY_RESULT,
                ...keyResult,
                currentValue: keyResult.startValue,
            }));
            appliedKeyResultsRef.current = keyResults;
            setValue('keyResults', keyResults, { shouldValidate: true, shouldDirty: true });
        }
        onDescriptionReplaced();
        showSuccessToast({ message: t('objectives.form.ai.success') });
    };

    return {
        isEnabled: isFeatureEnabled(FeatureFlag.OBJECTIVE_AI_DRAFT),
        modal,
        open,
        close,
        generateDraft,
        applyDraft,
    };
};
